import Modal from '../../components/Modal/Modal'
import { FormProvider, useForm } from 'react-hook-form'
import TextInput from '../../components/FormElements/TextInput'
import { Button } from '../../components/ui/Button'
import { useCSVReader } from 'react-papaparse'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isEmpty, omit } from 'lodash'
import ColumnsMapper from './ColumnsMapper'
import { columnOptions } from '../../lib/utils/columnMapperOptions'
import SelectInput from '../../components/FormElements/SelectInput'
import { useCreateUpload } from '../../api/uploads/useCreateUpload'
import { useGetTransactionTypes } from '../../api/utils/useGetTransactionTypes'
import { useGetCurrencies } from '../../api/utils/useGetCurrencies'
import toast from 'react-hot-toast'
import GainStrategyOptions from '../../components/HelpScreens/GainStrategyOptions'
import { queryClient } from '../../main'

type FieldValues = {
  upload_name: string
  upload_description: string
  upload_type: { label: string; value: string }
  currency: { label: string; value: string }
  end_currency: { label: string; value: string }
  gain_strategy: { label: string; value: string }
}

const CreateUpload = (): JSX.Element => {
  const { CSVReader } = useCSVReader()
  const { mutateAsync: createUpload } = useCreateUpload()
  const [fileData, setFileData] = useState<{
    results?: {
      data: Array<Record<string, unknown>>
    }
    file?: File
  }>({})
  const navigate = useNavigate()
  const formContext = useForm<FieldValues>({
    mode: 'onSubmit',
  })
  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, errors },
    setError,
    watch,
    reset,
    getValues,
    setValue,
  } = formContext

  const formData = watch()

  const columns = useMemo(() => {
    if (!fileData.results) return []
    return Object.keys(fileData.results.data[0])
      .filter((key) => key !== 'id')
      .filter((key) => !isEmpty(key))
      .map((key) => {
        return {
          accessor: key,
          Header: key,
          columns: [{ accessor: key, Header: key }],
        }
      })
  }, [fileData.results])

  const { data: transactionTypes } = useGetTransactionTypes()
  const { data: currencies } = useGetCurrencies()

  const transactionTypeOptions = useMemo(
    () =>
      transactionTypes?.map((d) => {
        return {
          label: d.name,
          value: d.id,
        }
      }),
    [transactionTypes],
  )

  const currencyOptions = useMemo(
    () =>
      currencies?.map((d) => {
        return {
          label: `${d.name} (${d.symbol})`,
          value: d.id,
        }
      }),
    [currencies],
  )

  const validateColumns = useCallback(
    (values: FieldValues) => {
      if (
        Number(values?.upload_type?.value) === 1 &&
        !['date', 'gain'].every((key) =>
          Object.values(values)
            ?.filter((d) => typeof d === 'object' && d !== null && 'value' in d)
            ?.map((d) => (d as { label: string; value: string }).value)
            ?.filter(Boolean)
            .includes(key),
        )
      ) {
        setError('root.columns', {
          type: 'custom',
          message: 'Required columns: Transaction Date, Dividend Amount',
        })
        return false
      } else if (Number(values?.upload_type?.value) === 2) {
        if (
          Number(values?.gain_strategy.value) === 1 &&
          !['date', 'gain'].every((key) =>
            Object.values(values)
              ?.filter((d) => typeof d === 'object' && d !== null && 'value' in d)
              ?.map((d) => (d as { label: string; value: string }).value)
              ?.filter(Boolean)
              .includes(key),
          )
        ) {
          setError('root.columns', {
            type: 'custom',
            message: 'Required columns: Sale Date, Gain/Loss',
          })
          return false
        } else if (
          Number(values?.gain_strategy.value) === 2 &&
          !['date', 'purchase_date', 'proceeds', 'cost_basis'].every((key) =>
            Object.values(values)
              ?.filter((d) => typeof d === 'object' && d !== null && 'value' in d)
              ?.map((d) => (d as { label: string; value: string }).value)
              ?.filter(Boolean)
              .includes(key),
          )
        ) {
          setError('root.columns', {
            type: 'custom',
            message: 'Required columns: Purchase Date, Sale Date, Proceeds, Cost Basis',
          })
          return false
        }
      } else if (
        Number(values?.upload_type?.value) === 3 &&
        !['date', 'proceeds', 'cost_basis'].every((key) =>
          Object.values(values)
            ?.filter((d) => typeof d === 'object' && d !== null && 'value' in d)
            ?.map((d) => (d as { label: string; value: string }).value)
            ?.filter(Boolean)
            .includes(key),
        )
      ) {
        setError('root.columns', {
          type: 'custom',
          message: 'Required columns: Transaction Date, Amount Sent, Amount Received',
        })
        return false
      }

      return true
    },
    [setError],
  )

  const onSubmit = async (values: FieldValues) => {
    if (!fileData.file) {
      setError('root.file', {
        type: 'custom',
        message: 'This field is required',
      })
    }

    const validated = validateColumns(values)
    if (!validated) return

    const toastId = toast.loading('Uploading file...')

    const rows = omit(values, [
      'upload_name',
      'upload_description',
      'upload_type',
      'currency',
    ])
    const mapped_columns = Object.entries(rows)
      .filter(([key, value]) => value)
      .map(([key, value]) => {
        return { [key]: value.value }
      })

    createUpload(
      {
        file: fileData.file as File,
        mapped_columns,
        upload_data: {
          upload_name: values?.upload_name,
          upload_description: values?.upload_description,
          transaction_type_id: Number(values?.upload_type?.value),
          currency_id: Number(values?.currency?.value),
          ...(Number(values?.upload_type?.value) === 2
            ? {
                gain_strategy: Number(values?.gain_strategy?.value),
              }
            : {}),
          ...(Number(values?.upload_type?.value) === 3
            ? {
                end_currency_id: Number(values?.end_currency?.value),
              }
            : {}),
        },
      },
      {
        onSuccess: (data) => {
          toast.success('File uploaded successfully!', { id: toastId })
          queryClient.invalidateQueries([
            'uploads',
            'list',
            { pageIndex: 1, pageSize: 10 },
          ])
          navigate('/dashboard/uploads')
        },
        onError: (error, variables, context) => {
          // @ts-ignore
          toast.error(error?.response?.data.message || 'An error occured', {
            id: toastId,
          })
          navigate('/dashboard/uploads')
        },
      },
    )

    return
  }

  const upload_type = watch('upload_type')

  return (
    <Modal key={'CreateUpload'} isOpen onClose={() => navigate('/dashboard/uploads')}>
      <FormProvider {...formContext}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col px-4 sm:px-8 py-6 w-full dark:bg-slate-800">
          <div className="flex flex-col gap-2 pb-8">
            <span className="text-foreground text-xl font-semibold">
              Create a New Upload
            </span>
            <div className={'font-medium pt-2  text-xs text-foreground/80'}>
              Accepted formats: CSV (50 mb max)
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextInput
                name="upload_name"
                label="Enter a name for your upload"
                type={'text'}
                required
                placeholder="Dividends - 2024"
              />
              <SelectInput
                required
                name={'upload_type'}
                label={'Transaction Type'}
                options={transactionTypeOptions ?? []}
              />
            </div>
            <TextInput
              name="upload_description"
              label="Enter a description for your upload"
              type={'text'}
              placeholder="Dividends imported from Schwab"
            />
            <div className="grid sm:grid-cols-2 gap-4">
              <SelectInput
                required
                name={'currency'}
                label={Number(upload_type?.value) === 3 ? 'Currency sent' : 'Currency'}
                options={currencyOptions ?? []}
                infoMessage={
                  Number(upload_type?.value) === 3
                    ? ''
                    : 'Currency displayed on the transaction sheet'
                }
              />
              {Number(upload_type?.value) === 2 && (
                <SelectInput
                  required
                  name={'gain_strategy'}
                  label={'Method for calculating gains'}
                  options={[
                    {
                      label: 'Sale Date',
                      value: 1,
                    },
                    {
                      label: 'Sale date - Purchase date',
                      value: 2,
                    },
                  ]}
                  helpMessage={<GainStrategyOptions />}
                />
              )}
              {Number(upload_type?.value) === 3 && (
                <SelectInput
                  required
                  name={'end_currency'}
                  label={'Currency received'}
                  options={currencyOptions ?? []}
                />
              )}
            </div>
          </div>
          {!fileData.file && (
            <div className={'pt-12 w-full px-6'}>
              <CSVReader
                onUploadAccepted={(
                  results: { data: Array<Record<string, unknown>> },
                  file: File,
                ) => {
                  if (results.data.length > 0) {
                    const _data = results?.data
                      .slice(0, 200)
                      .map((d: Record<string, unknown>, index: number) => {
                        return { ...d, id: index }
                      })
                    setFileData({
                      results,
                      file,
                    })
                  }
                }}
                onDragOver={(event: DragEvent) => {
                  event.preventDefault()
                }}
                onDragLeave={(event: DragEvent) => {
                  event.preventDefault()
                }}
                config={{
                  header: true,
                  dynamicTyping: true,
                  skipEmptyLines: 'greedy',
                }}>
                {({ getRootProps }: { getRootProps: any }) => (
                  <div
                    className={`font-medium align-center border-[3px] border-dashed ${
                      errors?.root?.file
                        ? 'border-[#F00]'
                        : 'border-silver dark:border-slate-400 dark:hover:border-white'
                    } radius-[10px] flex flex-column justify-center p-5 h-full w-full hover:border-[#686868]`}
                    {...getRootProps()}>
                    <div className={'flex flex-col items-center'}>
                      <div className={'py-2'}>Drag and drop files here</div>
                      <Button type={'button'} variant={'outline'}>
                        Upload
                      </Button>
                      {errors?.root?.file && (
                        <span className="font-bold text-xs text-[#F00]">
                          You must submit a file
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </CSVReader>
            </div>
          )}
          <div className="mt-6" />
          <div className="flex flex-row items-center gap-2 mb-2">
            {fileData.file && (
              <div className="flex flex-row gap-2 items-center">
                <Button
                  variant={'destructive'}
                  size={'sm'}
                  onClick={(e) => {
                    e.preventDefault()
                    setFileData({})
                  }}>
                  Remove file
                </Button>
              </div>
            )}
            {errors?.root?.columns.message && (
              <span className="text-xs font-bold text-[#F00]">
                {errors?.root?.columns?.message}
              </span>
            )}
          </div>
          {fileData.file && fileData.results && (
            <div className=" w-full h-full overflow-scroll flex h-full relative">
              <div className={'absolute top-0 left-0 w-full h-full overflow-scroll'}>
                <ColumnsMapper
                  data={fileData.results.data}
                  columns={columns}
                  rowsOptions={columnOptions}
                />
              </div>
            </div>
          )}

          <div>
            <div
              className={
                'items-center flex justify-between border-t-2 py-4 mt-8 absolute bottom-0  left-0 w-full px-8 bg-white dark:bg-slate-700'
              }>
              <Button
                variant={'secondary'}
                disabled={isSubmitting}
                onClick={(e) => {
                  e.preventDefault()
                  navigate('/dashboard/uploads')
                }}>
                Close
              </Button>
              <Button disabled={isSubmitting} type={'submit'}>
                Import
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default CreateUpload
